import { TrialService } from './trial.service';
import { Injectable } from '@angular/core';
import { Trial } from '../models/trial';
import { DataService } from './data.service';
import { ExperimentStage } from '../models/experimentStageEnum';
import { EmotionOption } from '../models/emotionOptionEnum';
import { ExperimentProgress } from '../models/experimentProgress';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class ExperimentService {
  public participantIdentifier: string | undefined = undefined;
  public trialSequenceNumber: number | undefined = undefined;

  currentStage = ExperimentStage.StartInstruction;

  currentStartInstructionPage = 1;

  startInstructionMessages = [
    `在此測試中，你將會看見一些面孔。仔細看每張面孔，並嘗試確定那人的情緒。<br>點擊最能描述那人感受的詞語。<br>如果那人並沒有顯示任何情緒，請點擊"沒有情緒"。`,
    `試試用一張面孔來練習一下...`,
    '開始練習',
  ];

  currentReadyStartPage = 1;

  readyStartMessages = [
    '現在開始測試。<br>記住要仔細看每張面，盡可能快和準確。',
    '開始測試',
  ];

  public practiceTrial: Trial | undefined = undefined;
  practicImage = 'practice.png';
  practiceCorrectAnswer = EmotionOption.ANGER;

  trials: Trial[] = [];

  public currentTrialNumber = 0; // 0 stands for practice
  public currentTrial: Trial | undefined = undefined;
  private lastValidTrial: Trial | undefined = undefined;

  // For pausing experiment before next experimental trial, not for saving in progress
  public pauseExperimentBeforeNextTrial = false;

  constructor(
    private dataService: DataService,
    private trialService: TrialService,
    private dbService: NgxIndexedDBService,
    private http: HttpClient
  ) {}

  public async loadExperimentResource() {
    if (!this.trialSequenceNumber) {
      throw new Error('Trial sequence number not set');
    }

    try {
      // Load trial data from JSON file
      const trialData: any = await firstValueFrom(
        this.http.get(
          `assets/data/trial-sequence-${this.trialSequenceNumber}.json`
        )
      );

      // Create trials with images from loaded data
      this.practiceTrial = {
        trialNumber: 0,
        imageFilename: this.practicImage,
        correctAnswer: this.practiceCorrectAnswer,
      };
      this.trials = [];
      for (let i = 0; i < trialData.trialsImages.length; ++i) {
        let correctAnswer: EmotionOption;
        switch (trialData.trialsCorrectAnswers[i]) {
          case 'NEUTRAL':
            correctAnswer = EmotionOption.NEUTRAL;
            break;
          case 'ANGRY':
            correctAnswer = EmotionOption.ANGER;
            break;
          case 'FEAR':
            correctAnswer = EmotionOption.FEAR;
            break;
          case 'HAPPY':
            correctAnswer = EmotionOption.HAPPY;
            break;
          case 'DISGUST':
            correctAnswer = EmotionOption.DISGUST;
            break;
          case 'SAD':
            correctAnswer = EmotionOption.SAD;
            break;
          default:
            console.log(trialData.trialsImages[i]);
            throw new Error(
              `Unknown emotion option: ${trialData.trialsCorrectAnswers[i]}`
            );
        }

        let newTrial: Trial = {
          trialNumber: i + 1,
          imageFilename: trialData.trialsImages[i],
          correctAnswer: correctAnswer,
        };
        this.trials.push(newTrial);
      }
      this.pauseExperimentBeforeNextTrial = false;
    } catch (error) {
      console.error('Error loading trial sequence data:', error);
      throw error;
    }
  }

  async initExperiment(
    participantIdentifier: string,
    trialSequenceNumber: number
  ) {
    this.participantIdentifier = participantIdentifier;
    this.trialSequenceNumber = trialSequenceNumber;

    await this.loadExperimentResource();

    // For saved in progress
    this.currentStage = ExperimentStage.StartInstruction;
    this.currentStartInstructionPage = 1;
    this.currentReadyStartPage = 1;
    this.currentTrialNumber = 0;
    this.currentTrial = this.practiceTrial;
    this.lastValidTrial = this.practiceTrial;
    this.dataService.resetData();
    this.trialService.prepareForStart();

    await this.saveProgress();
  }

  isOnExperimentTrial() {
    return (
      this.currentStage === ExperimentStage.Training ||
      this.currentStage === ExperimentStage.Experiment
    );
  }

  isOnExperimentInstruction() {
    return !this.isOnExperimentTrial();
  }

  isOnIntensityRatingDemo() {
    return this.currentStage === ExperimentStage.IntensityRatingDemo;
  }

  experimentInstructionMessage() {
    switch (this.currentStage) {
      case ExperimentStage.StartInstruction:
        return this.startInstructionMessages[
          this.currentStartInstructionPage - 1
        ];
      case ExperimentStage.IntensityRatingDemo:
        return ''; // This stage will use its own component, not instruction message
      case ExperimentStage.ReadyStart:
        return this.readyStartMessages[this.currentReadyStartPage - 1];
      case ExperimentStage.Pause:
        return '請稍候...';
      default:
        return '測試完成';
    }
  }

  public async goToNextTrial() {
    ++this.currentTrialNumber;
    this.currentTrial = this.trials[this.currentTrialNumber - 1];
    this.lastValidTrial = this.currentTrial;
    this.dataService.createNewResponse(
      this.currentTrialNumber,
      this.currentTrial?.correctAnswer
    );
    this.trialService.prepareForNextTrial();
    this.dataService.startCountingClassification();

    await this.saveProgress();
  }

  public async goToNextState() {
    if (this.currentStage === ExperimentStage.StartInstruction) {
      if (this.currentStartInstructionPage === 1) {
        this.currentStage = ExperimentStage.IntensityRatingDemo;
      } else if (this.currentStartInstructionPage < 3) {
        ++this.currentStartInstructionPage;
      } else {
        this.currentStage = ExperimentStage.Training;
      }
      await this.saveProgress();
      return;
    }
    if (this.currentStage === ExperimentStage.IntensityRatingDemo) {
      this.currentStage = ExperimentStage.StartInstruction;
      this.currentStartInstructionPage = 2;
      await this.saveProgress();
      return;
    }
    if (this.currentStage === ExperimentStage.Training) {
      this.currentStage = ExperimentStage.ReadyStart;
      await this.saveProgress();
      return;
    }
    if (this.currentStage === ExperimentStage.ReadyStart) {
      if (this.currentReadyStartPage < 2) {
        ++this.currentReadyStartPage;
      } else {
        this.currentStage = ExperimentStage.Experiment;
      }
      await this.saveProgress();
      return;
    }
    if (this.currentStage === ExperimentStage.Experiment) {
      this.currentStage = ExperimentStage.Complete;
      await this.saveProgress();
    }
  }

  public pauseExperimentRequested() {
    if (this.currentStage === ExperimentStage.Experiment) {
      this.pauseExperimentBeforeNextTrial = true;
    }
  }

  public pauseExperiment() {
    this.currentStage = ExperimentStage.Pause;
  }

  public continueExperiment() {
    this.currentStage = ExperimentStage.Experiment;
    this.pauseExperimentBeforeNextTrial = false;
  }

  public isLastTrial() {
    return this.currentTrialNumber === this.trials.length;
  }

  // Method to hide trial image but keep the trial data for saving
  public hideTrialImage() {
    this.lastValidTrial = this.currentTrial;
    this.currentTrial = undefined;
  }

  async saveProgress() {
    if (!this.participantIdentifier) {
      alert(
        'Error in saving progress - cannot find the participant identifier'
      );
    }
    if (!this.lastValidTrial) {
      alert('Error in saving progress - cannot find the current trial');
    }

    let progress: ExperimentProgress = {
      participantIdentifier: this.participantIdentifier,
      currentStage: this.currentStage,
      currentStartInstructionPage: this.currentStartInstructionPage,
      currentReadyStartPage: this.currentReadyStartPage,
      currentTrialNumber: this.currentTrialNumber,
      currentTrial: this.lastValidTrial,
    };

    await firstValueFrom(
      this.dbService.update('participants', {
        participantIdentifier: this.participantIdentifier,
        trialSequenceNumber: this.trialSequenceNumber,
        experimentProgress: progress,
        updateTimeUTC: new Date().toISOString(),
      })
    );
  }

  async loadProgress(progress: ExperimentProgress, trialSequenceNumber: number) {
    this.participantIdentifier = progress.participantIdentifier;
    this.trialSequenceNumber = trialSequenceNumber;
    
    // Verify consistency between participants and responses stores
    if (this.participantIdentifier) {
      try {
        const response: any = await firstValueFrom(
          this.dbService.getByKey('responses', this.participantIdentifier)
        );
        if (response && response.trialSequenceNumber && response.trialSequenceNumber !== this.trialSequenceNumber) {
          throw new Error(`Trial sequence number mismatch detected for ${this.participantIdentifier}. Participants store: ${this.trialSequenceNumber}, Responses store: ${response.trialSequenceNumber}. Data integrity compromised.`);
        }
      } catch (error) {
        // If it's our custom error, re-throw it
        if (error instanceof Error && error.message.includes('Trial sequence number mismatch')) {
          throw error;
        }
        // No responses exist yet, which is fine
        console.log('No responses found for participant, which is expected for new or early-stage experiments');
      }
    }
    
    await this.loadExperimentResource();

    if (!this.participantIdentifier) {
      alert(
        'Error in loading progress - cannot find the participant identifier'
      );
    }
    if (progress.currentStage === ExperimentStage.Experiment) {
      try {
        let correctEmotion: EmotionOption;
        if (!progress.currentTrial?.correctAnswer) {
          // Get correct answer from the trials array
          const trial = this.trials[progress.currentTrialNumber - 1];
          correctEmotion = trial?.correctAnswer || EmotionOption.NEUTRAL;
        } else {
          correctEmotion = progress.currentTrial.correctAnswer;
        }
        await this.dataService.loadParticipantResponses(
          progress.participantIdentifier,
          progress.currentTrialNumber,
          correctEmotion
        );
      } catch (e) {
        alert(e);
      }
    }

    this.currentStage = progress.currentStage;
    this.currentStartInstructionPage = progress.currentStartInstructionPage;
    this.currentReadyStartPage = progress.currentReadyStartPage;
    this.currentTrialNumber = progress.currentTrialNumber;
    this.currentTrial = progress.currentTrial;
    this.lastValidTrial = progress.currentTrial;

    if (this.currentStage === ExperimentStage.Training) {
      this.trialService.prepareForStart();
    }
    if (this.currentStage === ExperimentStage.Experiment) {
      this.dataService.startCountingClassification();
      this.trialService.prepareForNextTrial();
    }
  }

  getCurrentTrialImgSrc() {
    return `assets/images/${this.currentTrial?.imageFilename}`;
  }

  getPreloadTrialImgSrc() {
    if (this.isLastTrial()) {
      return this.getCurrentTrialImgSrc();
    } else {
      return `assets/images/${
        this.trials[this.currentTrialNumber + 1]?.imageFilename
      }`;
    }
  }
}
